import { useEffect, useId, useRef } from 'react';
import { ApiRequestError } from '../api.js';
import { formatDuration } from '../board.js';
import { useReplay } from '../queries.js';
import type { View } from '../route.js';
import { Board } from './Board.js';
import type { ChampionItem } from './GuessInput.js';

interface Props {
  gameId: string;
  byId: ReadonlyMap<string, ChampionItem>;
  go: (v: View) => void;
}

export function ReplayPage({ gameId, byId, go }: Props) {
  const id = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const replay = useReplay(gameId);
  const data = replay.data;
  useEffect(() => headingRef.current?.focus(), [data?.game.id]);

  const nameOf = (championId: string): string => byId.get(championId)?.name ?? championId;

  if (replay.isError) {
    const missing = replay.error instanceof ApiRequestError && replay.error.status === 404;
    return (
      <section className="panel" aria-labelledby={`${id}-h`}>
        <h2 id={`${id}-h`}>Replay</h2>
        <p role="alert">
          {missing ? 'That game was not found.' : 'Could not load this game. Try again.'}
        </p>
        <div className="actions">
          {missing ? null : (
            <button type="button" onClick={() => void replay.refetch()}>
              Retry
            </button>
          )}
          <button type="button" className="link" onClick={() => go('stats')}>
            Back to your stats
          </button>
        </div>
      </section>
    );
  }
  if (!data) {
    return (
      <p className="muted" role="status">
        Loading…
      </p>
    );
  }

  const { game, analysis, par } = data;
  const won = game.status === 'won';
  const n = game.guesses.length;
  const answer = nameOf(game.answer?.championId ?? '');

  return (
    <section className="panel panel--wide" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} ref={headingRef} tabIndex={-1}>
        Replay: {answer}
      </h2>
      <p className="muted">
        {won
          ? `Solved in ${n} ${n === 1 ? 'guess' : 'guesses'}`
          : `Gave up after ${n} ${n === 1 ? 'guess' : 'guesses'}`}
        {' · '}
        {formatDuration(game.elapsedMs)}
        {' · '}
        the solver needs {par} for this answer
      </p>

      <Board guesses={game.guesses} byId={byId} />

      <h3>Guess by guess</h3>
      <div className="tablewrap">
        <table className="analysis">
          <caption className="sr-only">How each guess scored, in the order you made them</caption>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Guess</th>
              <th scope="col">Could be the answer</th>
              <th scope="col">Candidates left</th>
              <th scope="col">Efficiency</th>
            </tr>
          </thead>
          <tbody>
            {analysis.map((a) => {
              const g = game.guesses.find((x) => x.seq === a.seq);
              return (
                <tr key={a.seq}>
                  <th scope="row">{a.seq}</th>
                  <td>{nameOf(g?.championId ?? '')}</td>
                  <td>{a.consistent ? 'Yes' : 'No'}</td>
                  <td>
                    {a.candidatesBefore} → {a.candidatesAfter}
                  </td>
                  <td>{Math.round(a.efficiency * 100)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="actions">
        <button type="button" onClick={() => go('stats')}>
          Back to your stats
        </button>
        <button type="button" className="link" onClick={() => go('play')}>
          Back to the game
        </button>
      </div>
    </section>
  );
}
